import React, { useEffect, useRef, useState } from 'react';

const MIN_PERCENT = 20;
const MAX_PERCENT = 80;
const KEY_STEP = 5; // how far one arrow-key press moves the bar, in percent

const clamp = (value) => Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, value));

// Storage can be blocked (private windows, strict settings); the split then just
// isn't remembered.
const readStoredPercent = (storageKey, fallback) => {
  try {
    const stored = Number(localStorage.getItem(storageKey));
    return stored ? clamp(stored) : fallback;
  } catch {
    return fallback;
  }
};

/**
 * Two panes side by side with a bar between them that is dragged like VS Code's
 * editor split: it turns blue on hover and while dragging, arrow keys (and
 * Home/End) move it too, and a double-click puts it back to `defaultPercent`.
 * `storageKey` remembers where the student left it.
 *
 * The widths key off the nearest container query (LearningZonePage's wrapper):
 * below 768px the panes stack and the bar hides. `end` is its own container, so
 * anything inside it that uses container queries sizes to the pane, not the page.
 */
export default function ResizableSplit({ start, end, label, defaultPercent = 40, storageKey }) {
  const containerRef = useRef(null);
  const [percent, setPercent] = useState(() =>
    storageKey ? readStoredPercent(storageKey, defaultPercent) : defaultPercent
  );
  const [dragging, setDragging] = useState(false);

  // Saved once a drag ends rather than on every pointer move.
  useEffect(() => {
    if (dragging || !storageKey) return;
    try {
      localStorage.setItem(storageKey, String(percent));
    } catch {
      // See readStoredPercent.
    }
  }, [percent, dragging, storageKey]);

  const percentAt = (clientX) => {
    const rect = containerRef.current.getBoundingClientRect();
    return rect.width ? ((clientX - rect.left) / rect.width) * 100 : percent;
  };

  const handlePointerDown = (event) => {
    if (event.button > 0) return; // primary button or touch only
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragging(true);
  };

  const handlePointerMove = (event) => {
    if (dragging) setPercent(clamp(percentAt(event.clientX)));
  };

  const handlePointerEnd = () => setDragging(false);

  const handleKeyDown = (event) => {
    const next = {
      ArrowLeft: percent - KEY_STEP,
      ArrowRight: percent + KEY_STEP,
      Home: MIN_PERCENT,
      End: MAX_PERCENT,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    setPercent(clamp(next));
  };

  // While dragging, the panes ignore the pointer so a PDF viewer (an iframe) or
  // the canvas can't swallow the drag when the pointer runs over them.
  const paneDragClass = dragging ? 'pointer-events-none select-none' : '';

  return (
    <div
      ref={containerRef}
      style={{ '--split': `${percent}%` }}
      className={`flex flex-col gap-4 [@container_(min-width:768px)]:flex-row [@container_(min-width:768px)]:gap-0 ${
        dragging ? 'cursor-col-resize select-none' : ''
      }`}
    >
      <div
        className={`flex min-w-0 flex-col [@container_(min-width:768px)]:w-[calc(var(--split)_-_6px)] [@container_(min-width:768px)]:shrink-0 ${paneDragClass}`}
      >
        {start}
      </div>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={label}
        aria-valuenow={Math.round(percent)}
        aria-valuemin={MIN_PERCENT}
        aria-valuemax={MAX_PERCENT}
        tabIndex={0}
        title="Drag to resize"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onLostPointerCapture={handlePointerEnd}
        onDoubleClick={() => setPercent(defaultPercent)}
        onKeyDown={handleKeyDown}
        className="group relative hidden w-3 shrink-0 cursor-col-resize touch-none select-none justify-center outline-none [@container_(min-width:768px)]:flex"
      >
        <span
          aria-hidden="true"
          className={`h-full rounded-full transition-all ${
            dragging
              ? 'w-1 bg-blue-500'
              : 'w-px bg-gray-200 group-hover:w-1 group-hover:bg-blue-500 group-focus-visible:w-1 group-focus-visible:bg-blue-500'
          }`}
        />
        {/* A grip in the middle so it's clear the bar can be moved. */}
        <span
          aria-hidden="true"
          className={`absolute left-1/2 top-1/2 flex h-12 w-3 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-1 rounded-full border bg-white shadow-sm transition-colors ${
            dragging ? 'border-blue-500' : 'border-gray-300 group-hover:border-blue-500 group-focus-visible:border-blue-500'
          }`}
        >
          {[0, 1, 2].map((dot) => (
            <span
              key={dot}
              className={`h-1 w-1 rounded-full ${dragging ? 'bg-blue-500' : 'bg-gray-400 group-hover:bg-blue-500'}`}
            />
          ))}
        </span>
      </div>

      <div
        className={`min-w-0 [container-type:inline-size] [@container_(min-width:768px)]:flex-1 ${paneDragClass}`}
      >
        {end}
      </div>
    </div>
  );
}
